/**
 * TypeScript types matching the Nordex Rust backend API.
 * These types mirror the structs defined in the `engine` crate.
 * 
 * Platform-agnostic - works in both web and React Native.
 */

// Order side (buy/sell)
export type OrderSide = "buy" | "sell";

// Order type (limit/market)
export type OrderType = "limit" | "market";

// Order status lifecycle
export type OrderStatus =
  | "pending"
  | "open"
  | "partially_filled"
  | "filled"
  | "cancelled"
  | "rejected";

// Order structure from the backend
export interface Order {
  id: string;
  user_id: string;
  market: string;
  side: OrderSide;
  order_type: OrderType;
  price: string; // Decimal as string for precision
  size: string;
  filled_size: string;
  status: OrderStatus;
  leverage?: string; // Leverage multiplier (defaults to 1x)
  reduce_only?: boolean; // If true, order can only reduce position
  slippage_tolerance?: string; // Max slippage for market orders
  take_profit?: string; // Take profit price (for limit orders)
  stop_loss?: string; // Stop loss price (for limit orders)
  /** Original USD value when user placed order in USD mode (null = calculate from size × price) */
  notional_value?: string;
  created_at: string; // ISO 8601
  updated_at: string;
}

// Position structure from the backend
export interface Position {
  user_id: string;
  market: string;
  size: string; // Positive = long, negative = short
  entry_price: string;
  mark_price: string;
  unrealized_pnl: string;
  realized_pnl: string;
  leverage: string;
  liquidation_price: string | null;
  margin: string;
  opened_at: string;
  updated_at: string;
  take_profit: string | null; // Take profit price
  stop_loss: string | null; // Stop loss price
}

// Margin mode types
export type MarginMode = "isolated" | "cross";

// Account/balance information
export interface Account {
  user_id: string;
  collateral: string;
  available_margin: string;
  used_margin: string;
  total_unrealized_pnl: string;
  total_realized_pnl: string;
  margin_mode: MarginMode;
  updated_at: string;
}

// Orderbook level (price + size)
export interface OrderBookLevel {
  price: string;
  size: string;
}

// Orderbook snapshot
export interface OrderBookSnapshot {
  bids: OrderBookLevel[];
  asks: OrderBookLevel[];
}

// Fill type describes what effect the trade had on the position
export type FillType = "open" | "partial_close" | "full_close" | "flip" | "liquidation";

// Fill (trade execution) from the backend
export interface Fill {
  id: string;
  order_id: string;
  user_id: string;
  market: string;
  side: OrderSide;
  size: string;
  price: string;
  fee: string;
  realized_pnl: string;
  fill_type: FillType;
  created_at: string;
}

// Funding rate for a market
export interface FundingRate {
  market: string;
  rate: string;
  next_funding_at: string;
  updated_at: string;
}

// Market statistics including open interest and volume
export interface MarketStats {
  market: string;
  market_price?: string;
  oracle_price?: string;
  open_interest_usd: string;
  volume_24h_usd: string;
  funding_rate: string;
  next_funding_at: string;
  price_change_24h?: string;
  price_change_percent_24h?: string;
  price_24h_ago?: string;
}

export interface MarketStatsResponse {
  stats: MarketStats[];
}

// Funding payment applied to a position
export interface FundingPayment {
  id: string;
  user_id: string;
  market: string;
  position_size: string;
  funding_rate: string;
  payment: string;
  created_at: string;
}

// Margin warning levels
export type MarginWarningLevel = "safe" | "caution" | "warning" | "danger";

// ============ User Notifications ============

export type NotificationType =
  | "order_filled"
  | "tp_sl_triggered"
  | "liquidation"
  | "deposit"
  | "withdrawal"
  | "account_security"
  | "near_liquidation";

export type NotificationSeverity = "info" | "success" | "warning" | "error";

export interface UserNotification {
  id: string;
  user_id: string;
  type: NotificationType;
  severity: NotificationSeverity;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  read: boolean;
  read_at?: string;
  created_at: string;
}

export interface NotificationPreferences {
  user_id: string;
  order_filled_enabled: boolean;
  order_filled_toast: boolean;
  tp_sl_enabled: boolean;
  tp_sl_toast: boolean;
  liquidation_enabled: boolean;
  liquidation_toast: boolean;
  deposit_enabled: boolean;
  deposit_toast: boolean;
  withdrawal_enabled: boolean;
  withdrawal_toast: boolean;
  account_security_enabled: boolean;
  account_security_toast: boolean;
  near_liquidation_enabled: boolean;
  near_liquidation_toast: boolean;
  created_at: string;
  updated_at: string;
}

// Engine events streamed via WebSocket
export type EngineEvent =
  | { type: "order_placed"; order: Order }
  | {
      type: "order_filled";
      order_id: string;
      fill_price: string;
      fill_size: string;
      timestamp: string;
    }
  | { type: "order_cancelled"; order_id: string; timestamp: string }
  | { type: "position_updated"; position: Position }
  | {
      type: "position_liquidated";
      user_id: string;
      market: string;
      liquidation_price: string;
      timestamp: string;
    }
  | {
      type: "deposit_credited";
      user_id: string;
      amount: string;
      tx_signature: string;
      new_balance: string;
      timestamp: string;
    }
  | {
      type: "margin_warning";
      user_id: string;
      market: string;
      margin_ratio: string;
      distance_to_liquidation_percent: string;
      warning_level: MarginWarningLevel;
      timestamp: string;
    }
  | {
      type: "liquidation_executed";
      liquidation_id: string;
      user_id: string;
      market: string;
      position_size: string;
      entry_price: string;
      liquidation_price: string;
      mark_price: string;
      pnl: string;
      penalty_fee: string;
      insurance_fund_contribution: string;
      timestamp: string;
    }
  | {
      type: "tp_sl_triggered";
      user_id: string;
      market: string;
      trigger_type: string; // "take_profit" or "stop_loss"
      trigger_price: string;
      execution_price: string;
      position_size: string;
      timestamp: string;
    }
  | {
      type: "withdrawal_status_changed";
      user_id: string;
      withdrawal_id: string;
      status: string;
      amount: string;
      token: string;
      destination_address: string;
      rejection_reason?: string;
      tx_signature?: string;
      timestamp: string;
    }
  | {
      type: "account_status_changed";
      user_id: string;
      old_status: string;
      new_status: string;
      reason?: string;
      admin_email?: string;
      timestamp: string;
    }
  | {
      type: "balance_adjusted";
      user_id: string;
      amount: string;
      reason: string;
      admin_email: string;
      new_balance: string;
      timestamp: string;
    };

// API request payloads

export interface CreateOrderRequest {
  market: string;
  side: OrderSide;
  order_type: OrderType;
  price: string;
  size: string;
  /** Optional leverage multiplier (defaults to 1x on backend) */
  leverage?: string;
  /** If true, order can only reduce an existing position (skip margin checks) */
  reduce_only?: boolean;
  /** Maximum acceptable slippage for market orders (0.005 = 0.5%). Null/undefined = unlimited */
  slippage_tolerance?: string;
  /** Time-in-force policy for limit orders */
  time_in_force?: "GTC" | "ALO" | "IOC";
  /** Take profit price (for limit orders) - applied to position when order fills */
  take_profit?: string;
  /** Stop loss price (for limit orders) - applied to position when order fills */
  stop_loss?: string;
  /** Original USD value when user places order in USD mode (null = calculate from size × price) */
  notional_value?: string;
}

export interface CreateAccountRequest {
  /** @deprecated Collateral is now server-authoritative. This field is ignored by the backend. */
  collateral?: string;
}

export interface DepositWithdrawRequest {
  amount: string;
}

export interface UpdateTpSlRequest {
  take_profit?: string | null;
  stop_loss?: string | null;
}

export interface UpdateTpSlResponse {
  position: Position;
  message: string;
}

// Partial TP/SL Targets types
export interface TpSlTarget {
  target_type: "take_profit" | "stop_loss";
  trigger_price: string;
  close_amount_type: "percentage" | "absolute";
  close_amount_value: string; // 50 for 50%, or 2.5 for 2.5 BTC
}

export interface SetTpSlTargetsRequest {
  targets: TpSlTarget[];
}

export interface TpSlTargetDto {
  id: string; // UUID
  target_type: "take_profit" | "stop_loss";
  trigger_price: string;
  close_amount: string; // calculated base currency amount
  close_amount_type: "percentage" | "absolute";
  close_amount_value: string;
  status: "active" | "triggered" | "cancelled";
}

export interface TpSlTargetsResponse {
  message: string;
  targets: TpSlTargetDto[];
  position: Position;
}

export interface GetTpSlTargetsResponse {
  targets: TpSlTargetDto[];
}

export interface UpdateLeverageRequest {
  leverage: string;
}

export interface UpdateLeverageResponse {
  position: Position;
  old_leverage: string;
  new_leverage: string;
  old_margin: string;
  new_margin: string;
  message: string;
}

export interface UpdatePositionMarginRequest {
  margin_delta: string; // Positive = add margin, negative = remove margin
}

export interface UpdatePositionMarginResponse {
  position: Position;
  old_margin: string;
  new_margin: string;
  old_liquidation_price: string | null;
  new_liquidation_price: string | null;
  message: string;
}

export interface PreviewPositionMarginRequest {
  margin_delta: string;
}

export interface PreviewPositionMarginResponse {
  new_margin: string;
  new_liquidation_price: string | null;
  current_margin: string;
  current_liquidation_price: string | null;
}

export interface UpdateMarginModeRequest {
  margin_mode: MarginMode;
}

export interface UpdateMarginModeResponse {
  margin_mode: MarginMode;
  message: string;
}

export interface UpdateFavoriteMarketsRequest {
  favorite_markets: string[];
}

export interface FavoriteMarketsResponse {
  favorite_markets: string[];
}

export interface DepositWithdrawResponse {
  success: boolean;
  previous_balance: string;
  new_balance: string;
  amount: string;
}

// API response wrappers

export interface OrderResponse {
  order: Order;
  position?: Position; // Position included for immediate feedback (market orders)
  realized_pnl?: string; // Realized P&L for position closes/reduces (Decimal as string)
}

export interface OrderListResponse {
  orders: Order[];
}

export interface PositionResponse {
  position: Position;
}

export interface PositionListResponse {
  positions: Position[];
}

export interface AccountResponse {
  account: Account;
  /** Total notional value of all open positions */
  total_position_value: string;
  /** Account leverage = total_position_value / equity */
  account_leverage: string;
  /** Margin ratio = (equity / used_margin) * 100 */
  margin_ratio: string;
}

export interface FillListResponse {
  fills: Fill[];
}

export interface FundingRatesResponse {
  rates: FundingRate[];
}

export interface FundingPaymentsResponse {
  payments: FundingPayment[];
}

export interface ApiError {
  error: string;
}

// Deposit system types

export type DepositStatus = "pending" | "confirmed" | "credited" | "failed";

export interface DepositAddress {
  deposit_address: string;
  user_id: string;
}

export interface Deposit {
  id: string;
  user_id: string;
  deposit_address: string;
  tx_signature: string;
  token_mint: string;
  amount: string;
  amount_usd: string;
  status: DepositStatus;
  created_at: string;
  credited_at: string | null;
}

export interface DepositAddressResponse {
  deposit_address: string;
  user_id: string;
  /** Blockchain chain: "solana", "arbitrum", "ethereum" */
  chain: string;
  /** Previous address if migrated (security rotation) */
  migrated_from?: string;
  /** Reason for migration */
  migration_reason?: string;
}

export interface DepositListResponse {
  deposits: Deposit[];
}

// Withdrawal system types

export type WithdrawalStatus = "pending" | "approved" | "processing" | "completed" | "failed" | "rejected";

export interface Withdrawal {
  id: string;
  user_id: string;
  destination_address: string;
  amount: string;
  amount_usd: string;
  token_mint: string;
  tx_signature: string | null;
  status: WithdrawalStatus;
  auto_approved: boolean;
  rejection_reason: string | null;
  created_at: string;
  approved_at: string | null;
  processed_at: string | null;
  /** Whether the withdrawal was processed by the background auto-processor */
  auto_processed?: boolean;
  /** Security rule names that flagged this for manual review */
  security_flags?: string[] | null;
  /** Snapshot of user's total net deposits at withdrawal creation time */
  net_deposit_at_creation?: number | null;
  /** Human-readable token symbol (e.g. "USDC", "USDT") */
  token_symbol?: string | null;
  /** Blockchain chain name (e.g. "solana", "arbitrum", "ethereum") */
  chain?: string | null;
}

export interface CreateWithdrawalRequest {
  destination_address: string;
  amount: string;
  /** 2FA code (required if user has 2FA enabled) */
  totp_code?: string;
  /** Token symbol to withdraw (USDC or USDT). Defaults to USDC for backward compatibility. */
  token_symbol?: string;
}

export interface CreateWithdrawalResponse {
  id: string;
  status: string;
  auto_approved: boolean;
  message: string;
}

export interface WithdrawalListResponse {
  withdrawals: Withdrawal[];
}

// Saved Withdrawal Addresses types

export interface SavedWithdrawalAddress {
  id: string;
  user_id: string;
  address: string;
  label: string;
  created_at: string;
}

export interface SaveWithdrawalAddressRequest {
  address: string;
  label: string;
}

export interface SaveWithdrawalAddressResponse {
  address: SavedWithdrawalAddress;
}

export interface SavedWithdrawalAddressesResponse {
  addresses: SavedWithdrawalAddress[];
}

// Two-Factor Authentication (2FA) types

export interface TwoFactorInitResponse {
  /** Base32-encoded TOTP secret (for manual entry) */
  secret: string;
  /** otpauth:// URI for QR code generation */
  qr_uri: string;
  /** Issuer name shown in authenticator app */
  issuer: string;
}

export interface TwoFactorVerifySetupRequest {
  /** 6-digit TOTP code from authenticator app */
  code: string;
}

export interface TwoFactorVerifySetupResponse {
  success: boolean;
  /** One-time backup codes (shown only once) */
  backup_codes: string[];
  message: string;
}

export interface TwoFactorDisableRequest {
  /** Current TOTP code to confirm disable */
  code: string;
}

export interface TwoFactorDisableResponse {
  success: boolean;
  message: string;
}

export interface TwoFactorStatusResponse {
  /** Whether 2FA is enabled for the user */
  enabled: boolean;
  /** When 2FA was enabled */
  enabled_at?: string;
}

export interface TwoFactorRegenerateBackupRequest {
  /** Current TOTP code to confirm */
  code: string;
}

export interface TwoFactorRegenerateBackupResponse {
  success: boolean;
  /** New backup codes (replaces old ones) */
  backup_codes: string[];
  message: string;
}

// Liquidation system types

export interface Liquidation {
  id: string;
  user_id: string;
  market: string;
  position_size: string;
  entry_price: string;
  liquidation_price: string;
  mark_price: string;
  pnl: string;
  penalty_fee: string;
  insurance_fund_contribution: string;
  timestamp: string;
}

export interface LiquidationsResponse {
  liquidations: Liquidation[];
}

export interface PositionMarginHealth {
  market: string;
  size: string;
  entry_price: string;
  mark_price: string;
  liquidation_price: string | null;
  margin_ratio: string;
  distance_to_liquidation_percent: string;
  warning_level: MarginWarningLevel;
}

export interface MarginHealthResponse {
  user_id: string;
  positions: PositionMarginHealth[];
  account_margin_ratio: string | null;
  account_warning_level: MarginWarningLevel | null;
}

// VLP (Virtual Liquidity Pool) types

export interface VlpQuote {
  market: string;
  bid_price: string;
  ask_price: string;
  spread: string;
  oracle_price: string;
  vlp_position: string;
  timestamp: string;
}

export interface VlpQuotesResponse {
  quotes: VlpQuote[];
}

export interface VlpQuoteResponse {
  quote: VlpQuote;
}

// Faucet types (Devnet only)

export interface FaucetResponse {
  success: boolean;
  message: string;
  previous_balance: string;
  new_balance: string;
  amount: string;
}

// Wallet export types (DEPRECATED - removed for security)
// Privy users should use Privy's built-in exportWallet feature

export interface WalletExportRequest {
  // This endpoint has been removed for security reasons
}

export interface WalletExportResponse {
  public_key: string;
  private_key: string;
  derivation_index: number;
  warning: string;
}

// Utility type helpers

export function parseDecimal(value: string | number | null | undefined): number {
  if (value == null || value === "") return 0;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

export function toDecimalString(value: number, precision = 8): string {
  return value.toFixed(precision);
}

// Convert backend Position to frontend-compatible format
export function toFrontendPosition(pos: Position) {
  const size = parseDecimal(pos.size);
  return {
    id: `${pos.user_id}-${pos.market}`, // Composite key
    market: pos.market,
    side: size >= 0 ? ("long" as const) : ("short" as const),
    size: Math.abs(size),
    entryPrice: parseDecimal(pos.entry_price),
    currentPrice: parseDecimal(pos.mark_price),
    leverage: parseDecimal(pos.leverage),
    liquidationPrice: pos.liquidation_price
      ? parseDecimal(pos.liquidation_price)
      : 0,
    unrealizedPnl: parseDecimal(pos.unrealized_pnl),
    realizedPnl: parseDecimal(pos.realized_pnl),
    margin: parseDecimal(pos.margin),
    status: "open" as const,
    openedAt: new Date(pos.opened_at),
    updatedAt: new Date(pos.updated_at),
    takeProfit: pos.take_profit ? parseDecimal(pos.take_profit) : undefined,
    stopLoss: pos.stop_loss ? parseDecimal(pos.stop_loss) : undefined,
  };
}

// Convert frontend order params to backend request
export function toBackendOrderRequest(
  market: string,
  side: "long" | "short",
  size: number,
  price: number,
  orderType: "market" | "limit",
  options?: { 
    reduceOnly?: boolean; 
    leverage?: number; 
    slippageTolerance?: number | null; 
    timeInForce?: "GTC" | "ALO" | "IOC";
    takeProfit?: number | null;
    stopLoss?: number | null;
    /** Original USD value when user places order in USD mode */
    notionalValue?: number | null;
  }
): CreateOrderRequest {
  const request: CreateOrderRequest = {
    market,
    side: side === "long" ? "buy" : "sell",
    order_type: orderType,
    price: toDecimalString(price),
    size: toDecimalString(size),
  };
  
  if (options?.reduceOnly) {
    request.reduce_only = true;
  }
  
  if (options?.leverage) {
    request.leverage = toDecimalString(options.leverage, 2);
  }
  
  // Include slippage tolerance for market orders (null = Auto = unlimited, omit from request)
  if (options?.slippageTolerance !== undefined && options?.slippageTolerance !== null && orderType === "market") {
    request.slippage_tolerance = toDecimalString(options.slippageTolerance, 6);
  }
  
  // Add time_in_force for limit orders
  if (orderType === "limit" && options?.timeInForce) {
    request.time_in_force = options.timeInForce;
  }
  
  // Add take_profit and stop_loss for all order types (market and limit)
  if (options?.takeProfit !== undefined && options?.takeProfit !== null) {
    request.take_profit = toDecimalString(options.takeProfit, 8);
  }
  if (options?.stopLoss !== undefined && options?.stopLoss !== null) {
    request.stop_loss = toDecimalString(options.stopLoss, 8);
  }
  
  // Add notional_value when user placed order in USD mode
  // This preserves the exact USD value the user intended (e.g., $5,000)
  if (options?.notionalValue !== undefined && options?.notionalValue !== null) {
    request.notional_value = toDecimalString(options.notionalValue, 2);
  }
  
  return request;
}

// Fees configuration from backend
export interface FeesConfig {
  maker_fee_bps: string;
  taker_fee_bps: string;
  maker_fee_percentage: string;
  taker_fee_percentage: string;
}

// Order Preview Request/Response (Backend Preview API - Industry Standard)

export interface OrderPreviewRequest {
  market: string;
  side: "long" | "short";
  size: string; // Decimal string
  price?: string; // Optional, for limit orders
  leverage: string; // Decimal string
  order_type: "market" | "limit";
}

export interface OrderPreviewResponse {
  estimated_liquidation_price: string;
  required_margin: string;
  estimated_fee: string;
  position_size: string;
  estimated_entry_price: string;
  leverage: string;
  maintenance_margin_rate: string;
  liquidation_penalty_rate: string;
  available_balance_after: string;
  used_margin_after: string;
  sufficient_balance: boolean;
  warning: string | null;
}

